/* eslint-disable array-bracket-newline */
import { useReducer, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount, useNetwork } from 'wagmi';
import BigNumber from 'bignumber.js';
import { Network } from '@kattana-io/common';
import Swap from './Swap';
import Limit from './Limit';
import LimitSettings from './Limit/Settings';
import CurrencyMenu from './CurrencyMenu';
import RecentTrades from './RecentTrades';
import {
  EOverlays,
  ETabs,
  getInitialTradingState,
  ICoin,
  TradingActions,
  tradingReducer,
} from '../reducers/tradingReducer';
import { IStore } from '../../../reducers';
import { KattanaAPI } from '../../../store/createStore';
import { actionCreateNotification } from '../../../actions/notifications';

import '../styles.scss';
import { useWallet } from '../../../wallet/walletContext';

const useAppState = () => {
  // Global state
  const current = useSelector((state: IStore) => state.current);
  const globalDispatch = useDispatch();
  const onNotify = (args: any) =>
    globalDispatch(actionCreateNotification(args));

  return {
    current,
    onNotify,
  };
};

interface IDexTradingProps {
  height: string | number;
}

const DexTrading = ({ height }: IDexTradingProps) => {
  const { current, onNotify } = useAppState();
  const { chain } = useNetwork();
  const { address } = useAccount();
  const {
    state: { tokenBalancesByAddr, tokenRatesByAddr, tokenBalancesBySym },
    onRefresh: onWalletRefresh,
  } = useWallet();
  const [state, dispatch] = useReducer(
    tradingReducer,
    getInitialTradingState(current)
  );

  useEffect(() => {
    if (chain?.id != state.network?.toDecimal()) {
      dispatch({
        payload: Network.fromChainId(chain?.id),
        type: TradingActions.SetNetwork,
      });
    }
  }, [chain]);

  useEffect(() => {
    const { base, quote } = getInitialTradingState(current);
    dispatch({ type: TradingActions.SetBaseCoin, payload: base });
    dispatch({ type: TradingActions.SetQuoteCoin, payload: quote });
  }, [current]);

  useEffect(() => {
    const { base, quote } = getInitialTradingState(current);
    if (
      state.base.address.toLowerCase() === base.address.toLowerCase() &&
      state.quote.address.toLowerCase() === quote.address.toLowerCase()
    ) {
      dispatch({ type: TradingActions.SetMeta, payload: current });
    } else {
      KattanaAPI.tickerByNetworkBaseAndQuote(
        current.chain,
        state.base.address,
        state.quote.address
      ).then((ticker) => {
        if (ticker && ticker.address) {
          const flipped =
            state.base.address.toLowerCase() !==
            ticker.base_address.toLowerCase();
          dispatch({
            type: TradingActions.SetMeta,
            payload: { ...ticker, flipped },
          });
        } else {
          dispatch({ type: TradingActions.SetMeta, payload: undefined });
        }
      });
    }
  }, [state.base.address, state.quote.address]);

  const setTab = (tab: ETabs) =>
    dispatch({ type: TradingActions.SetTradingTab, payload: tab });

  const setOverlay = (overlay: EOverlays) =>
    dispatch({ type: TradingActions.SetOverlay, payload: overlay });

  /**
   * Get top overlay to avoid unnecessary rerender
   * @param overlay
   * @returns
   */
  const getOverlay = (overlay: EOverlays, base: ICoin, quote: ICoin) => {
    switch (overlay) {
    case EOverlays.BaseCurrencyMenu:
      return (
        <CurrencyMenu
          selected={state.base}
          restricted={state.quote}
          balances={tokenBalancesBySym}
          balancesMapping={tokenBalancesByAddr}
          walletCoinsOnly={true}
          onSelect={(item: ICoin) => {
            if (quote.address == item.address) {
              setOverlay(EOverlays.None);
              return;
            }
            dispatch({ type: TradingActions.SetBaseCoin, payload: item });
            dispatch({
              type: TradingActions.SetQuoteAmount,
              payload: BigNumber(0),
            });
            setOverlay(EOverlays.None);
          }}
          onCloseMenu={() => setOverlay(EOverlays.None)}
        />
      );
    case EOverlays.QuoteCurrencyMenu:
      return (
        <CurrencyMenu
          selected={state.quote}
          restricted={state.base}
          balances={tokenBalancesBySym}
          balancesMapping={tokenBalancesByAddr}
          walletCoinsOnly={false}
          onSelect={(item: ICoin) => {
            if (base.address == item.address) {
              setOverlay(EOverlays.None);
              return;
            }
            dispatch({ type: TradingActions.SetQuoteCoin, payload: item });
            dispatch({
              type: TradingActions.SetBaseAmount,
              payload: BigNumber(0),
            });
            setOverlay(EOverlays.None);
          }}
          onCloseMenu={() => setOverlay(EOverlays.None)}
        />
      );
    default:
      return null;
    }
  };

  /**
   * Get contents of the widget
   * @param tab
   * @returns
   */
  const getTradingContents = (tab: ETabs) => {
    switch (tab) {
    case ETabs.Swap:
      return (
        <Swap
          current={current}
          account={address}
          chainId={chain?.id}
          base={state.base}
          quote={state.quote}
          dexChainId={chain?.id}
          tab={state.tradingTab}
          meta={state.meta}
          setTab={setTab}
          setOverlay={setOverlay}
          onWalletRefresh={onWalletRefresh}
          onNotify={onNotify}
          onFlip={() => dispatch({ type: TradingActions.Flip })}
          setSlippage={(slippage) =>
            dispatch({ type: TradingActions.SetSlippage, payload: slippage })
          }
          balancesAddress={tokenBalancesByAddr}
          ratesAddress={tokenRatesByAddr}
          onSetBaseAmount={(payload: BigNumber) =>
            dispatch({ type: TradingActions.SetBaseAmount, payload })
          }
          onSetQuoteAmount={(payload: BigNumber) =>
            dispatch({ type: TradingActions.SetQuoteAmount, payload })
          }
          slippage={state.slippage}
        />
      );
    case ETabs.Limit:
      return (
        <Limit
          current={current}
          account={address}
          chainId={chain?.id}
          base={state.base}
          quote={state.quote}
          dexChainId={chain?.id}
          tab={state.tradingTab}
          meta={state.meta}
          setTab={setTab}
          setOverlay={setOverlay}
          onWalletRefresh={onWalletRefresh}
          onNotify={onNotify}
          onFlip={() => dispatch({ type: TradingActions.Flip })}
          balancesAddress={tokenBalancesByAddr}
          ratesAddress={tokenRatesByAddr}
          onSetBaseAmount={(payload: BigNumber) =>
            dispatch({ type: TradingActions.SetBaseAmount, payload })
          }
          onSetQuoteAmount={(payload: BigNumber) =>
            dispatch({ type: TradingActions.SetQuoteAmount, payload })
          }
          onSetTradesCount={(payload: number) =>
            dispatch({ type: TradingActions.SetTradesCount, payload })
          }
          slippage={state.slippage}
          tradesCount={state.tradesCount}
          sendTokens={state.sendTokens}
        />
      );
    case ETabs.RecentTrades:
      return (
        <RecentTrades
          setTab={setTab}
          account={address}
          base={state.base}
          quote={state.quote}
          dexChainId={chain?.id}
          tab={state.tradingTab}
        />
      );
    case ETabs.LSettings:
      return (
        <LimitSettings
          setTab={setTab}
          price={state.limitPrice}
          slippage={state.slippage}
          sendTokens={state.sendTokens}
          setSlippage={(slippage) =>
            dispatch({ type: TradingActions.SetSlippage, payload: slippage })
          }
          deadline={state.deadline}
          setDeadline={(deadline) =>
            dispatch({ type: TradingActions.SetDeadline, payload: deadline })
          }
          setSendTokens={(sendTokens) =>
            dispatch({
              type: TradingActions.SetSendTokens,
              payload: sendTokens,
            })
          }
        />
      );
    default:
      return <div>Unknown state</div>;
    }
  };

  const hasOverlay = state.overlay != EOverlays.None;
  const overlay = getOverlay(state.overlay, state.base, state.quote);
  const contents = getTradingContents(state.tradingTab);

  return (
    <div
      className={`trading ${hasOverlay ? 'trading--overlay' : ''}`}
      style={{ flexBasis: height }}
    >
      {overlay}
      {contents}
    </div>
  );
};

export default DexTrading;
